<template>
  <div>
    <Head :title="title" />
    <div class="flex flex-wrap gap-6">
      <div class="bg-white rounded-md shadow overflow-hidden max-w-3xl flex-1">
        <form @submit.prevent="update">
          <div class="p-8 -mr-6 -mb-8 flex flex-wrap">
            <text-input v-model="form.name" :error="form.errors.name" class="pr-6 pb-8 w-full" label="Nombre" />

            <div class="pr-6 pb-8 w-full">
              <icon-picker v-model="form.icon" />
            </div>
          </div>

          <div class="px-8 py-4 bg-gray-50 border-t border-gray-100 flex justify-end items-center">
            <loading-button :loading="form.processing" class="btn-indigo" type="submit">Actualizar departamento</loading-button>
          </div>
        </form>
      </div>

      <div class="flex flex-col gap-6 w-full lg:w-96">
        <div class="bg-white rounded-md shadow overflow-hidden p-8">
          <h3 class="font-bold mb-1">Responsables</h3>
          <p class="text-sm text-gray-500 mb-4">Quienes tengan el check activo reciben por correo TODOS los tickets que se registren en este departamento, además de la asignación normal.</p>

          <ul class="mb-6">
            <li v-for="responsible in responsibles" :key="responsible.id" class="flex items-center justify-between py-2 border-b">
              <div class="flex flex-col">
                <span class="text-sm">{{ responsible.name }}</span>
                <span class="text-xs text-gray-400">{{ responsible.email }}</span>
              </div>
              <div class="flex items-center gap-3">
                <label class="flex items-center gap-1 text-xs cursor-pointer">
                  <input type="checkbox" :checked="responsible.notify_all" @change="toggleNotifyAll(responsible, $event.target.checked)" />
                  Recibir todos
                </label>
                <button type="button" class="text-red-600 text-xs hover:underline" @click="removeResponsible(responsible)">Quitar</button>
              </div>
            </li>
            <li v-if="responsibles.length === 0" class="text-sm text-gray-400 py-2">Sin responsables asignados.</li>
          </ul>

          <div class="border-t pt-4">
            <select-input-filter
              placeholder="Buscar usuario"
              :onInput="doFilterUsers"
              :items="filteredUsers"
              v-model="newResponsible.user_id"
              class="w-full mb-3"
              label="Agregar responsable"
            ></select-input-filter>

            <label class="flex items-center gap-2 text-sm mb-3 cursor-pointer">
              <input type="checkbox" v-model="newResponsible.notify_all" />
              Recibir todos los correos de este departamento
            </label>

            <button type="button" class="btn-indigo w-full" :disabled="!newResponsible.user_id" @click="addResponsible">Agregar</button>
          </div>
        </div>

        <div class="bg-white rounded-md shadow overflow-hidden p-8">
          <h3 class="font-bold mb-1">Sedes</h3>
          <p class="text-sm text-gray-500 mb-4">Sedes del colegio donde existe este departamento. Los tickets registrados en cada sede quedan vinculados aquí.</p>

          <ul class="mb-6">
            <li v-for="sede in sedes" :key="sede.id" class="flex items-center justify-between py-2 border-b">
              <span class="text-sm">{{ sede.sede }}</span>
              <div class="flex items-center gap-3">
                <label class="flex items-center gap-1 text-xs cursor-pointer">
                  <input type="checkbox" :checked="sede.private" @change="toggleSedePrivate(sede, $event.target.checked)" />
                  Privado
                </label>
                <button type="button" class="text-red-600 text-xs hover:underline" @click="removeSede(sede)">Quitar</button>
              </div>
            </li>
            <li v-if="sedes.length === 0" class="text-sm text-gray-400 py-2">Sin sedes vinculadas.</li>
          </ul>

          <div class="border-t pt-4" v-if="available_sedes.length">
            <select-input v-model="newSede.department_id" class="w-full mb-3" label="Agregar sede">
              <option :value="null" />
              <option v-for="d in available_sedes" :key="d.id" :value="d.id">{{ d.name }}</option>
            </select-input>

            <button type="button" class="btn-indigo w-full" :disabled="!newSede.department_id" @click="addSede">Agregar</button>
          </div>
          <p v-else class="text-sm text-gray-400">Este departamento ya está vinculado a todas las sedes.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import SelectInput from '@/Shared/SelectInput'
import IconPicker from '@/Shared/IconPicker'
import LoadingButton from '@/Shared/LoadingButton'
import SelectInputFilter from '@/Shared/SelectInputFilter'
import { Head } from '@inertiajs/inertia-vue3'

export default {
  metaInfo() {
    return { title: this.form.name }
  },
  components: {
    LoadingButton,
    TextInput,
    SelectInput,
    SelectInputFilter,
    IconPicker,
    Head,
  },
  layout: Layout,
  props: {
    title: String,
    ticket_department: Object,
    responsibles: Array,
    users: Array,
    sedes: Array,
    available_sedes: Array,
  },
  remember: 'form',
  data() {
    return {
      filteredUsers: [],
      newResponsible: {
        user_id: null,
        notify_all: false,
      },
      newSede: {
        department_id: null,
      },
      form: this.$inertia.form({
        name: this.ticket_department.name,
        icon: this.ticket_department.icon,
      }),
    }
  },
  methods: {
    update() {
      this.form.put(this.route('ticket-departments.update', this.ticket_department.id))
    },
    doFilterUsers(e) {
      const search = e.target.value.toLowerCase()
      this.filteredUsers = this.users.filter(u => u.name.toLowerCase().includes(search)).slice(0, 10)
    },
    addResponsible() {
      this.$inertia.post(this.route('ticket-departments.responsibles.store', this.ticket_department.id), {
        user_id: this.newResponsible.user_id,
        notify_all: this.newResponsible.notify_all,
      }, {
        preserveScroll: true,
        onSuccess: () => {
          this.newResponsible = { user_id: null, notify_all: false }
        },
      })
    },
    toggleNotifyAll(responsible, checked) {
      this.$inertia.put(this.route('ticket-departments.responsibles.update', responsible.id), {
        notify_all: checked,
      }, { preserveScroll: true })
    },
    removeResponsible(responsible) {
      if (confirm('¿Quitar a ' + responsible.name + ' como responsable de este departamento?')) {
        this.$inertia.delete(this.route('ticket-departments.responsibles.destroy', responsible.id), { preserveScroll: true })
      }
    },
    addSede() {
      this.$inertia.post(this.route('ticket-departments.sedes.store', this.ticket_department.id), {
        department_id: this.newSede.department_id,
      }, {
        preserveScroll: true,
        onSuccess: () => {
          this.newSede = { department_id: null }
        },
      })
    },
    toggleSedePrivate(sede, checked) {
      this.$inertia.put(this.route('ticket-departments.sedes.update', sede.id), {
        private: checked,
      }, { preserveScroll: true })
    },
    removeSede(sede) {
      if (confirm('¿Quitar la sede "' + sede.sede + '" de este departamento?')) {
        this.$inertia.delete(this.route('ticket-departments.sedes.destroy', sede.id), { preserveScroll: true })
      }
    },
  },
}
</script>
